import type { FormKitSectionsSchema } from "@formkit/core";
import {
	createMessage,
	type FormKitNode,
	type FormKitPlugin,
	type FormKitTypeDefinition,
} from "@formkit/core";
import { findSection } from "@formkit/inputs";
import { clone } from "@formkit/utils";
import { createInput } from "@formkit/vue";
import { useDebounceFn } from "@vueuse/core";
import { markRaw, nextTick } from "vue";
import AppTooltipImport from "@/components/AppTooltip.vue";
import ApplicationFormSectionCard from "@/components/loan-application/ApplicationFormSectionCard.vue";
import LoanApplicationFormInputCurrency from "@/components/loan-application/loan-application-form/LoanApplicationFormInputCurrency.vue";
import { loanApplicationFormInputDate } from "@/components/loan-application/loan-application-form/LoanApplicationFormInputDate";
import LoanApplicationFormInputPercentage from "@/components/loan-application/loan-application-form/LoanApplicationFormInputPercentage.vue";
import LoanApplicationIDREFComponent from "@/components/loan-application/loan-application-form/LoanApplicationIDREFComponent.vue";
import type { ValidationError } from "@/generatedApi";
import { isKeyOf } from "@/helpers/typeHelpers";
import { useLoanApplicationFormOverrider } from "./useLoanApplicationFormOverrides";

const AppTooltip = markRaw(AppTooltipImport);

export function useHdnForm(
	validationFunc: (formData: any) => Promise<ValidationError[]>,
) {
	const { overrider } = useLoanApplicationFormOverrider();

	const schemaComponents = {
		loanApplicationFormInputDate: loanApplicationFormInputDate,
		loanApplicationFormInputCurrency: createInput(
			LoanApplicationFormInputCurrency,
		),
		loanApplicationFormInputPercentage: createInput(
			LoanApplicationFormInputPercentage,
		),
		IDREF: createInput(LoanApplicationIDREFComponent),
	} satisfies Record<string, FormKitTypeDefinition>;

	const schemaComponentsPlugin: FormKitPlugin = (node) => {
		if (!isKeyOf(schemaComponents, node.props.type)) {
			return;
		}

		const component = schemaComponents[node.props.type];
		node.define(component);
	};

	const removeLabelFromGroups: FormKitPlugin = (node) => {
		if (node.type === "list") {
			node.on("created", () => {
				if (!node.props.definition?.schema) {
					return;
				}

				const schemaFn = node.props.definition.schema;
				node.props.definition.schema = (sectionsSchema = {}) => {
					sectionsSchema.legend = { children: [] };

					if (typeof schemaFn === "function") {
						return schemaFn(sectionsSchema);
					}
					return schemaFn ?? [];
				};
			});
		}
	};

	const normalizeRepeaterValuePlugin: FormKitPlugin = (node) => {
		if (node.type !== "list") {
			return;
		}

		const normalizeListValue = (value: unknown) => {
			if (value === undefined || Array.isArray(value)) {
				return value;
			}

			if (value !== null && typeof value === "object") {
				return [value];
			}

			return value;
		};

		node.hook.input((value, next) => {
			return next(normalizeListValue(value));
		});

		node.on("created", ({ payload }) => {
			payload._value = normalizeListValue(payload._value);
			payload.value = normalizeListValue(payload.value);
		});
	};

	const normalizeGroupValuePlugin: FormKitPlugin = (node) => {
		if (node.type !== "group") {
			return;
		}

		const normalizeGroupValue = (value: unknown) => {
			if (!Array.isArray(value)) {
				return value;
			}

			return value[0];
		};

		node.hook.input((value, next) => {
			return next(normalizeGroupValue(value));
		});

		node.on("created", ({ payload }) => {
			payload._value = normalizeGroupValue(payload._value);
			payload.value = normalizeGroupValue(payload.value);
		});
	};

	const fieldNamePlugin: FormKitPlugin = (node) => {
		if (
			["button", "submit", "hidden", "group", "list", "meta"].includes(
				node.props.type,
			) ||
			node.type !== "input"
		) {
			return;
		}

		// TODO: dynamic toggle based on a dev environment
		return;
	};

	const applyDynamicOverridesPlugin: FormKitPlugin = (node) => {
		node.on("created", () => {
			if (!node.context) {
				return;
			}

			const nodeWithType =
				node.parent?.context?.type === "repeater" ? node.parent : node;

			const defaultInstanceLabelFn = (
				_: FormKitNode,
				indexOrName: number | string,
			) => {
				const label = nodeWithType.context?.label;

				if (typeof indexOrName === "string") {
					return label;
				}

				return `${label} (${indexOrName + 1})`;
			};
			node.context.fns.instanceLabel = defaultInstanceLabelFn;

			const rootNode = node.at("$root");
			if (!rootNode) {
				return;
			}
			rootNode.on("commit.deep", () => {
				if (!node.context?.fns.instanceLabel) {
					return;
				}

				const label = node.context.fns.instanceLabel(node, node.name);

				node.context.label = label;
			});

			const xsdType = nodeWithType.context?.attrs["data-xsd"];
			const name = nodeWithType.name;

			const overrides = overrider.byTypeAndName(xsdType, name);
			if (!overrides.fns?.instanceLabel) {
				return;
			}

			const instanceLabelFn = (instanceNode: FormKitNode, index: number) => {
				const label = overrides.fns?.instanceLabel?.(instanceNode, index);

				return label ?? defaultInstanceLabelFn(instanceNode, index);
			};

			node.context.fns.instanceLabel = instanceLabelFn;
		});
	};

	const populateVolgnummerPlugin: FormKitPlugin = (node) => {
		if (node.name === "Volgnummer") {
			node.on("created", () => {
				if (node.value) {
					return;
				}

				if (node.parent?.parent?.context?.type === "repeater") {
					// traverse children to find highest Volgnummer
					const repeaterNode = node.parent?.parent;
					const children = repeaterNode?.children;

					const highestVolgnummer = children.reduce((highest, child) => {
						if (!("children" in child)) {
							return highest;
						}

						const volgnummerNode = child.children.find(
							(child) => child.name === "Volgnummer",
						);

						const volgnummer = Number(volgnummerNode?.value ?? 0);
						return volgnummer > highest ? volgnummer : highest;
					}, 0);

					void node.input(highestVolgnummer + 1);
					return;
				}

				if (
					node.parent?.context?.type &&
					["group", "optionalGroup"].includes(node.parent.context.type)
				) {
					void node.input(1);
					return;
				}
			});
		}
	};

	const populateIDPlugin: FormKitPlugin = (node) => {
		if (node.name === "ID") {
			node.on("created", () => {
				if (node.value) {
					return;
				}

				const id = `LL-${crypto.randomUUID()}`;
				void node.input(id);
			});
		}
	};

	const deleteConfirmationPlugin: FormKitPlugin = (node) => {
		function confirmedRemove(label: string, removeFn: any) {
			const confirmed = confirm(
				`Weet je zeker dat je '${label}' wilt verwijderen? Dit kan niet ongedaan gemaakt worden.`,
			);

			if (confirmed) {
				return removeFn();
			}
		}

		if (node.context?.type === "repeater") {
			node.on("created", () => {
				const createRemover = node.context?.fns.createRemover;

				if (!node.context?.fns) {
					return;
				}

				node.context.fns.createRemover = (index: number) => {
					const removeFn = createRemover?.(index);

					return () => {
						let label = node.props.label;
						if (node.context?.fns.instanceLabel) {
							label = node.context.fns.instanceLabel(node, index);
						}

						confirmedRemove(label, removeFn);
					};
				};
			});

			return;
		}

		if (node.context?.type === "optionalGroup") {
			node.on("created", () => {
				const removeFn = node.context?.fns.disableGroup;

				if (!node.context) {
					return;
				}

				node.context.fns.disableGroup = () => {
					confirmedRemove(node.props.label, removeFn);
				};
			});

			return;
		}
	};

	function createHdnValidationPlugin(): FormKitPlugin {
		const hdnValidationSet = new Set<FormKitNode>();

		const hdnValidationPlugin = (node: FormKitNode) => {
			if (node.props.type !== "form") return;

			node.on("created", () => {
				if (!node.context?.fns) {
					return;
				}

				node.context.fns.manualHdnValidate = () =>
					performHdnValidation(node.value, node);
			});

			node.on("commit", ({ payload }) => {
				void performHdnValidationDebounced(payload, node);
			});
		};

		const performHdnValidationDebounced = useDebounceFn(
			performHdnValidation,
			1000,
		);

		let lastValidation = Date.now();

		async function performHdnValidation(payload: any, node: FormKitNode) {
			const startedAt = Date.now();

			const validationErrors = await validationFunc({
				OfferteAanvraag: payload,
			});

			// Discard this validation when the most recent validation is newer.
			if (startedAt < lastValidation) {
				return validationErrors;
			}
			lastValidation = startedAt;

			const [formErrors, fieldErrors] = hdnErrorsToFormKitErrors(
				validationErrors,
				node,
			);

			if (formErrors.length === 0 && Object.keys(fieldErrors).length === 0) {
				hdnValidationSet.forEach((node) => {
					node.store.remove(`${node.address.slice(1).join(".")}:hdn`);
				});
				hdnValidationSet.clear();
			}

			const oldHdnValidationSet = new Set(hdnValidationSet);
			Object.entries(fieldErrors).forEach((issue) => {
				const [path, messages] = issue;
				const targetNode = node.at(path);

				if (targetNode) {
					oldHdnValidationSet.delete(targetNode);

					targetNode.store.apply(
						messages.map((message) =>
							createMessage({
								blocking: true,
								type: "validation",
								key: `${path}:hdn`,
								value: message,
							}),
						),
					);

					targetNode.store.set(
						createMessage({
							key: "failing",
							value: true,
							visible: false,
						}),
					);

					hdnValidationSet.add(targetNode);
				}
			});

			oldHdnValidationSet.forEach((node) => {
				node.store.remove(`${node.address.slice(1).join(".")}:hdn`);
				node.store.remove("failing");
				hdnValidationSet.delete(node);
			});

			return validationErrors;
		}

		function hdnErrorsToFormKitErrors(
			hdnErrors: ValidationError[],
			node: FormKitNode,
		): [ValidationError[], Record<string, string[]>] {
			const fieldErrors: Record<string, string[]> = {};
			const formErrors: ValidationError[] = [];

			const getFormKitPath = (jsonPath?: string) =>
				jsonPath?.slice(3).replaceAll("[", ".").replaceAll("]", "");

			hdnErrors.forEach((hdnError) => {
				const path = getFormKitPath(hdnError.jsonPath);
				const targetNode = path ? node.at(path) : undefined;

				if (
					[
						"xsd/1839",
						"xsd/unexpected-element",
						"xsd/attribute-required-but-missing",
					].includes(hdnError.code)
				) {
					return;
				}

				if (targetNode && path) {
					fieldErrors[path] ??= [];
					fieldErrors[path].push(hdnError.message);
				} else {
					formErrors.push(hdnError);
				}
			});

			return [formErrors, fieldErrors];
		}

		return hdnValidationPlugin;
	}

	const tooltipPlugin: FormKitPlugin = (node) => {
		if (!node.props.help) {
			return;
		}

		node.on("created", () => {
			if (!node.props.definition) {
				return;
			}

			const inputDefinition = clone(node.props.definition);

			const originalSchema = inputDefinition.schema;
			if (typeof originalSchema !== "function") {
				return;
			}

			const higherOrderSchema = (extensions: FormKitSectionsSchema = {}) => {
				extensions.help = {
					$cmp: null,
					children: [],
				};

				const inputSchema = originalSchema(extensions);
				const finalSchema = Array.isArray(inputSchema)
					? inputSchema[0]
					: inputSchema;
				if (!finalSchema) {
					throw new Error("Schema is empty");
				}

				const [, labelSection] = findSection(
					finalSchema,
					node.context?.type === "radio" ? "legend" : "label",
				);

				if (typeof labelSection === "object") {
					const labelWithTooltip = {
						$cmp: AppTooltip,
						props: {
							tooltip: "$help",
						},
						children: [labelSection.else],
					};

					if ("else" in labelSection) {
						// @ts-expect-error - for some reason the types don't match. it works though...
						labelSection.else = labelWithTooltip;
					} else if ("then" in labelSection) {
						// @ts-expect-error - for some reason the types don't match. it works though...
						labelSection.then = labelWithTooltip;
					}
				}

				return inputSchema;
			};

			inputDefinition.schema = higherOrderSchema;
			if (inputDefinition.schemaMemoKey) {
				inputDefinition.schemaMemoKey += "-tooltip";
			}
			node.props.definition = inputDefinition;
		});
	};

	const legends = ["checkbox_multi", "radio_multi", "repeater", "transferlist"];
	const addAsteriskToRequiredFieldsPlugin: FormKitPlugin = (node) => {
		if (
			["button", "submit", "hidden", "group", "list", "meta"].includes(
				node.props.type,
			)
		)
			return;

		node.on("created", () => {
			const legendOrLabel = legends.includes(
				`${node.props.type}${node.props.options ? "_multi" : ""}`,
			)
				? "legend"
				: "label";

			if (node.props.definition?.schemaMemoKey) {
				node.props.definition.schemaMemoKey += `${node.props.options ? "_multi" : ""}_add_asterisk`;
			}

			const schemaFn = node.props.definition?.schema;
			if (!node.props.definition || typeof schemaFn !== "function") {
				return;
			}

			node.props.definition.schema = (sectionsSchema = {}) => {
				sectionsSchema[legendOrLabel] = {
					children: [
						"$label",
						{
							$el: "span",
							if: "$state.required",
							attrs: {
								class: "$classes.asterisk",
							},
							children: ["*"],
						},
					],
				};

				return schemaFn(sectionsSchema);
			};
		});
	};

	const hideInvalidFieldPlugin: FormKitPlugin = (node) => {
		if (node.props.type === "repeater") {
			return;
		}

		node.addProps(["filledOrInvalid"]);
		node.props.filledOrInvalid = false;

		node.on("created", () => {
			if (!node.props.definition) {
				return;
			}

			const inputDefinition = clone(node.props.definition);
			const originalSchema = inputDefinition.schema;
			if (typeof originalSchema !== "function") return;

			const higherOrderSchema = (extensions: FormKitSectionsSchema) => {
				extensions.outer = {
					attrs: {
						"data-filled-or-invalid": "$filledOrInvalid",
					},
				};
				return originalSchema(extensions);
			};

			inputDefinition.schema = higherOrderSchema;
			if (inputDefinition.schemaMemoKey) {
				inputDefinition.schemaMemoKey += "-filled-or-invalid";
			}
			node.props.definition = inputDefinition;
		});

		function determineNodeVisibility(node: FormKitNode) {
			if (!node.context) {
				return;
			}

			const isInvalid = Object.entries(node.context.messages).length > 0;

			if (node.value || isInvalid) {
				node.props.filledOrInvalid = true;
			} else {
				node.props.filledOrInvalid = false;
			}
		}

		node.on("message-added", () => {
			determineNodeVisibility(node);
		});

		node.on("message-updated", () => {
			determineNodeVisibility(node);
		});

		node.on("message-removed", () => {
			determineNodeVisibility(node);
		});

		node.on("commit", () => {
			determineNodeVisibility(node);
		});
	};

	const revalidateWhenEntityIsAdded: FormKitPlugin = (node) => {
		function validate() {
			void nextTick(() => {
				node.root.context?.fns.manualValidate?.();
			});
		}

		node.on("created", () => {
			if (node.context?.fns.createAppend) {
				const originalCreateAppend = node.context.fns.createAppend;

				function creator(...args: any[]) {
					const create = originalCreateAppend(...args);

					return () => {
						create();
						validate();
					};
				}

				node.context.fns.createAppend = creator;
				return;
			}

			if (node.context?.fns.enableGroup) {
				const originalEnableGroup = node.context.fns.enableGroup;

				function enableGroup() {
					originalEnableGroup();
					validate();
				}

				node.context.fns.enableGroup = enableGroup;
			}
		});
	};

	const manualFormValidator: FormKitPlugin = (node) => {
		if (node.props.type !== "form" || !node.context) {
			return;
		}

		/**
		 * This function will simulate a submit, without actually submitting the form. This will trigger the validation of the form.
		 */
		async function validateHandler() {
			const setSubmitted = (n: FormKitNode) =>
				n.store.set(
					createMessage({
						key: "submitted",
						value: true,
						visible: false,
					}),
				);

			if (node.value == null) {
				throw Error("Could not execute validateHandler: no formkit node");
			}

			node.walk(setSubmitted);
			setSubmitted(node);
			node.emit("submit-raw");

			return node.context?.fns.manualHdnValidate?.();
		}

		node.context.fns.manualValidate = validateHandler;
	};

	const plugins: FormKitPlugin[] = [
		schemaComponentsPlugin,
		fieldNamePlugin,
		removeLabelFromGroups,
		normalizeRepeaterValuePlugin,
		normalizeGroupValuePlugin,
		addAsteriskToRequiredFieldsPlugin,
		applyDynamicOverridesPlugin,
		populateVolgnummerPlugin,
		populateIDPlugin,
		deleteConfirmationPlugin,
		createHdnValidationPlugin(),
		tooltipPlugin,
		hideInvalidFieldPlugin,
		revalidateWhenEntityIsAdded,
		manualFormValidator,
	];

	function focusField(e: Event) {
		const eventTarget = e.target as HTMLElement;

		const targetIsSelect = eventTarget.tagName === "SELECT";
		const targetIsInput = eventTarget.tagName === "INPUT";
		const targetIsSelectable = targetIsInput || targetIsSelect;

		const inputTarget: HTMLElement | null = targetIsSelectable
			? eventTarget
			: eventTarget.querySelector("input, select, button");

		if (
			!targetIsSelect &&
			!(targetIsInput && document.activeElement === inputTarget)
		) {
			e.preventDefault();
			inputTarget?.focus({ focusVisible: true } as FocusOptions);
		}
	}

	function toggleCollapse(e: Event) {
		const parentDiv = (e.target as Element).closest("div");
		parentDiv?.classList.toggle("active");
	}

	const data = {
		focusField,
		toggleCollapse,
	};

	const library = markRaw({
		SectionCard: ApplicationFormSectionCard,
	});

	return { data, plugins, library };
}
